import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { BlossomUploader } from '@nostrify/nostrify/uploaders';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAppContext } from '@/hooks/useAppContext';
import { useMediaPreferences } from '@/hooks/useMediaPreferences';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { getEffectiveBlossomServers } from '@/lib/appBlossom';
import {
  dbClaimAnonymousItems,
  dbDeleteBlobOnly,
  dbDeleteMedia,
  dbEstimateUsage,
  dbGetAllItems,
  dbGetBlob,
  dbPutItem,
  dbPutMedia,
} from '@/lib/mediaDb';
import {
  collectTags,
  generateThumbnail,
  hashFile,
  mediaKindFromType,
  probeMedia,
  type MediaItem,
  type MediaUpload,
} from '@/lib/media';
import { sanitizeUrl } from '@/lib/sanitizeUrl';

/**
 * Module-level cache + subscriber list, matching the pattern used by
 * `useOfflineLibrary` and `useArticleDrafts` — so the media page, the
 * editor's insert dialog, and any thumbnail on screen all stay in sync
 * the moment something is added, tagged, uploaded, or deleted.
 *
 * Unlike those two, IndexedDB is **async**, so the cache starts as
 * `undefined` ("not loaded yet") rather than being readable
 * synchronously on first render. Consumers get an explicit `isLoading`
 * instead of a misleadingly-empty library.
 */
let cache: MediaItem[] | undefined;
let loadPromise: Promise<void> | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function ensureLoaded(): Promise<void> {
  if (cache) return Promise.resolve();
  loadPromise ??= dbGetAllItems<MediaItem>()
    .then((items) => {
      cache = items;
      emit();
    })
    .catch(() => {
      // IndexedDB unavailable (private mode, disabled storage). Treat as
      // an empty library rather than leaving consumers loading forever.
      cache = [];
      emit();
    })
    .finally(() => {
      loadPromise = null;
    });
  return loadPromise;
}

function setCache(next: MediaItem[]) {
  cache = next;
  emit();
}

/**
 * The module cache holds every account's items; per-account scoping is
 * applied where the hook reads it (see `useMediaLibrary`). Upserts and
 * removals must therefore match on the composite identity (pubkey +
 * sha256) — two accounts can each hold the same file.
 */
function upsert(item: MediaItem) {
  const current = cache ?? [];
  const index = current.findIndex((i) => i.sha256 === item.sha256 && i.pubkey === item.pubkey);
  if (index === -1) {
    setCache([item, ...current]);
  } else {
    const next = [...current];
    next[index] = item;
    setCache(next);
  }
}

/** Finds an item in the cache, scoped to an account. */
function findItem(pubkey: string, sha256: string): MediaItem | undefined {
  return (cache ?? []).find((i) => i.pubkey === pubkey && i.sha256 === sha256);
}

export interface AddMediaResult {
  item: MediaItem;
  /** True when this file was already in the library (matched by hash) rather than newly added. */
  duplicate: boolean;
}

/** NIP-94 file metadata kind. */
const FILE_METADATA_KIND = 1063;

/**
 * Optionally publishes a NIP-94 (kind 1063) event for a freshly uploaded
 * file, gated on the `publishNip94` preference.
 *
 * Fire-and-forget is deliberate: the metadata event is a nice-to-have
 * discoverability layer on top of the files themselves, so a relay
 * rejecting it must never make the *upload* look like it failed.
 */
function maybePublishNip94(
  publish: (event: { kind: number; content: string; tags: string[][] }) => void,
  enabled: boolean,
  item: MediaItem,
  url: string,
): void {
  if (!enabled) return;
  try {
    const tags: string[][] = [
      ['url', url],
      ['m', item.type],
      ['x', item.sha256],
      ['size', String(item.size)],
      ...(item.width && item.height ? [['dim', `${item.width}x${item.height}`]] : []),
      ...(item.alt?.trim() ? [['alt', item.alt.trim()]] : []),
    ];
    publish({ kind: FILE_METADATA_KIND, content: item.alt?.trim() ?? '', tags });
  } catch {
    // Best-effort only — see doc comment.
  }
}

/**
 * The local-first media library: files live in IndexedDB on this device,
 * optionally mirrored to Blossom servers, **scoped to the active Nostr
 * account**.
 *
 * Scoping is per account (`pubkey`) rather than per browser: two accounts
 * sharing a device each get their own library, and switching accounts
 * never shows one identity's files to another. Items added while logged
 * out land in an anonymous bucket that's claimed by the first account to
 * open the library while logged in (the pre-scoping library migrates the
 * same way — it belonged to whoever was using this browser).
 *
 * Only the *index* of files is scoped. The bytes themselves are keyed by
 * raw sha256 and shared across accounts, since content-addressed storage
 * makes the same file byte-identical either way (see `mediaDb.ts`).
 *
 * Local-first rather than Blossom-first is a deliberate choice. Blossom's
 * `GET /list/<pubkey>` endpoint is explicitly "optional and
 * unrecommended" (BUD-12), so a library can't be a live view of server
 * contents — it has to be its own index. Keeping that index local means
 * the library works offline, works logged out, and doesn't announce
 * every file the user has ever touched.
 */
export function useMediaLibrary() {
  const { user } = useCurrentUser();
  const { config } = useAppContext();
  const { publishNip94 } = useMediaPreferences();
  const { mutate: publishEvent } = useNostrPublish();
  const [allItems, setAllItems] = useState<MediaItem[] | undefined>(cache);

  /** '' while logged out — the anonymous bucket. */
  const pubkey = user?.pubkey ?? '';

  useEffect(() => {
    const listener = () => setAllItems(cache ? [...cache] : undefined);
    listeners.add(listener);
    void ensureLoaded();
    return () => {
      listeners.delete(listener);
    };
  }, []);

  // One-time migration: claim anonymous items (added while logged out, or
  // stored before per-account scoping existed) for the account currently
  // looking at the library. Idempotent — after the first claim no
  // anonymous items remain, so subsequent mounts find nothing to do.
  useEffect(() => {
    if (!pubkey) return;
    let cancelled = false;

    void (async () => {
      await ensureLoaded();
      if (cancelled) return;
      const legacy = (cache ?? []).filter((i) => !i.pubkey);
      if (legacy.length === 0) return;

      const claimed = new Set(await dbClaimAnonymousItems(pubkey));
      if (cancelled) return;
      console.info(`[media] Claimed ${claimed.size} unscoped item(s) for this account.`);
      setCache([
        ...(cache ?? []).filter((i) => i.pubkey),
        ...legacy.filter((i) => claimed.has(i.sha256)).map((i) => ({ ...i, pubkey })),
      ]);
    })();

    return () => {
      cancelled = true;
    };
  }, [pubkey]);

  const servers = useMemo(
    () => getEffectiveBlossomServers(config.blossomServerMetadata, config.useAppBlossomServers),
    [config.blossomServerMetadata, config.useAppBlossomServers],
  );

  const sorted = useMemo(
    () =>
      allItems
        ?.filter((i) => i.pubkey === pubkey)
        .sort((a, b) => b.addedAt - a.addedAt),
    [allItems, pubkey],
  );

  const tags = useMemo(() => collectTags(sorted ?? []), [sorted]);

  /**
   * Adds a file to the local library. Never uploads — that's an explicit,
   * separate action, so adding media works fully offline.
   */
  const add = useCallback(async (file: File): Promise<AddMediaResult> => {
    await ensureLoaded();

    const sha256 = await hashFile(file);
    const existing = findItem(pubkey, sha256);

    if (existing) {
      // Content-addressed storage makes dedupe free. If the bytes were
      // previously evicted, re-adding the same file restores them.
      if (!existing.hasBlob) {
        const kind = mediaKindFromType(file.type);
        const thumb = await generateThumbnail(file, kind);
        const restored: MediaItem = { ...existing, hasBlob: true };
        await dbPutMedia(restored, file, thumb);
        upsert(restored);
        return { item: restored, duplicate: true };
      }
      return { item: existing, duplicate: true };
    }

    const kind = mediaKindFromType(file.type);
    const [thumb, dims] = await Promise.all([generateThumbnail(file, kind), probeMedia(file, kind)]);

    const item: MediaItem = {
      sha256,
      pubkey,
      name: file.name || `${kind}-${sha256.slice(0, 8)}`,
      type: file.type || 'application/octet-stream',
      kind,
      size: file.size,
      width: dims.width,
      height: dims.height,
      duration: dims.duration,
      tags: [],
      addedAt: Date.now(),
      uploads: [],
      hasBlob: true,
    };

    await dbPutMedia(item, file, thumb);
    upsert(item);
    return { item, duplicate: false };
  }, [pubkey]);

  const update = useCallback(async (sha256: string, changes: Partial<MediaItem>) => {
    await ensureLoaded();
    const existing = findItem(pubkey, sha256);
    if (!existing) return;
    const next = { ...existing, ...changes, sha256: existing.sha256, pubkey: existing.pubkey };
    await dbPutItem(next);
    upsert(next);
  }, [pubkey]);

  /** Removes the item from this device entirely. Does not touch any Blossom server. */
  const remove = useCallback(async (sha256: string) => {
    await dbDeleteMedia(pubkey, sha256);
    setCache((cache ?? []).filter((i) => !(i.sha256 === sha256 && i.pubkey === pubkey)));
  }, [pubkey]);

  /**
   * Drops the local bytes but keeps the metadata and thumbnail. Only
   * offered for uploaded items — evicting a local-only file would
   * destroy the sole copy.
   */
  const evictBlob = useCallback(async (sha256: string) => {
    await ensureLoaded();
    const existing = findItem(pubkey, sha256);
    if (!existing || existing.uploads.length === 0) return;
    await dbDeleteBlobOnly(pubkey, sha256);
    const next = { ...existing, hasBlob: false };
    await dbPutItem(next);
    upsert(next);
  }, [pubkey]);

  const getBlob = useCallback((sha256: string) => dbGetBlob(sha256), []);

  /**
   * Re-downloads an item's bytes from its Blossom URL and restores them
   * locally — the inverse of `evictBlob`. Only meaningful for items that
   * are uploaded but have been evicted locally (`hasBlob: false`); if the
   * item already has its bytes, this is a no-op.
   *
   * The download goes through the same https-only allowlist the rest of
   * the app uses for external URLs (`sanitizeUrl`), not a raw fetch — a
   * media URL ultimately comes from a server response, so it's treated
   * as untrusted input like every other external URL.
   */
  const restoreBlob = useCallback(async (sha256: string): Promise<void> => {
    await ensureLoaded();
    const item = findItem(pubkey, sha256);
    if (!item) throw new Error('That file is no longer in your media library.');
    if (item.hasBlob) return;

    const url = sanitizeUrl(item.uploads[0]?.url);
    if (!url) throw new Error('This file has no hosted URL to download from.');

    const response = await fetch(url);
    if (!response.ok) throw new Error(`Download failed (${response.status}).`);
    const blob = await response.blob();

    // Restore the thumbnail too, so the grid immediately shows the file
    // again instead of an icon. Content-addressed storage means the blob
    // is identical to what was evicted, so the original hash still
    // matches and no re-indexing is needed.
    const thumb = await generateThumbnail(blob, item.kind);

    const next: MediaItem = { ...item, hasBlob: true };
    await dbPutMedia(next, blob, thumb);
    upsert(next);
  }, [pubkey]);

  /**
   * Uploads an item's bytes to every configured Blossom server.
   *
   * Uses `BlossomUploader` per-server rather than once with the whole
   * list, so one failing server doesn't abort the rest — partial success
   * is genuinely useful here, and we want to record exactly which
   * servers hold the blob.
   */
  const upload = useCallback(
    async (sha256: string): Promise<MediaItem> => {
      await ensureLoaded();
      const item = findItem(pubkey, sha256);
      if (!item) throw new Error('That file is no longer in your media library.');
      if (!user) throw new Error('You must be logged in to upload files.');
      if (servers.length === 0) throw new Error('No Blossom servers are configured.');

      const blob = await dbGetBlob(sha256);
      if (!blob) {
        throw new Error(
          "This file's data isn't stored on this device anymore, so it can't be uploaded again.",
        );
      }

      const file = new File([blob], item.name, { type: item.type });
      const alreadyOn = new Set(item.uploads.map((u) => u.server));
      const pending = servers.filter((server) => !alreadyOn.has(server));

      if (pending.length === 0) return item;

      const results = await Promise.allSettled(
        pending.map(async (server): Promise<MediaUpload> => {
          const uploader = new BlossomUploader({ servers: [server], signer: user.signer });
          const tags = await uploader.upload(file);
          const url = tags.find(([name]) => name === 'url')?.[1];
          if (!url) throw new Error(`${server} did not return a URL.`);
          return { server, url, uploadedAt: Date.now() };
        }),
      );

      const succeeded = results
        .filter((r): r is PromiseFulfilledResult<MediaUpload> => r.status === 'fulfilled')
        .map((r) => r.value);

      if (succeeded.length === 0) {
        const reason = results.find((r): r is PromiseRejectedResult => r.status === 'rejected')?.reason;
        throw new Error(reason instanceof Error ? reason.message : 'Every server rejected the upload.');
      }

      const next: MediaItem = { ...item, uploads: [...item.uploads, ...succeeded] };
      await dbPutItem(next);
      upsert(next);

      // Opt-in NIP-94 event for the first upload — see `useMediaPreferences`.
      const firstUrl = succeeded[0]?.url;
      if (firstUrl) maybePublishNip94(publishEvent, publishNip94, next, firstUrl);

      return next;
    },
    [servers, user, pubkey, publishEvent, publishNip94],
  );

  /**
   * Inserts or replaces a media item without touching `addedAt` — used
   * by the index sync, which must never pretend a synced-in item was
   * added on this device at restore time.
   *
   * The item is stamped with the current account's pubkey: the encrypted
   * index is per-account (NIP-44 to self), so anything pulled down by
   * definition belongs to whoever decrypted it.
   */
  const putSynced = useCallback(async (item: MediaItem) => {
    const stamped: MediaItem = { ...item, pubkey };
    await dbPutItem(stamped);
    upsert(stamped);
  }, [pubkey]);

  return {
    items: sorted,
    isLoading: sorted === undefined,
    tags,
    servers,
    add,
    update,
    remove,
    evictBlob,
    restoreBlob,
    getBlob,
    upload,
    putSynced,
  };
}

/**
 * Uploads any media a draft still references only locally, and returns
 * the content with those placeholders rewritten to real URLs.
 *
 * This is what makes "insert now, sort out hosting later" safe: an
 * article is never signed with a `wikistar-media:` placeholder, because
 * publishing resolves every one of them first and fails loudly if it
 * can't. The alternative — allowing a placeholder through — would
 * produce a permanently broken image for every reader, unrecoverable
 * once signed.
 */
export function useResolvePendingMedia() {
  const { upload, items } = useMediaLibrary();

  return useMutation({
    mutationFn: async (hashes: string[]): Promise<Map<string, string>> => {
      const urlByHash = new Map<string, string>();

      for (const hash of hashes) {
        const item = (items ?? []).find((i) => i.sha256 === hash);
        if (!item) {
          throw new Error(
            'An image in this article is no longer in your media library, so it can\u2019t be uploaded.',
          );
        }

        // Already hosted — nothing to do.
        const existing = item.uploads[0]?.url;
        if (existing) {
          urlByHash.set(hash, existing);
          continue;
        }

        const uploaded = await upload(hash);
        const url = uploaded.uploads[0]?.url;
        if (!url) throw new Error(`Could not upload "${item.name}".`);
        urlByHash.set(hash, url);
      }

      return urlByHash;
    },
  });
}

export async function getMediaStorageEstimate() {
  return dbEstimateUsage();
}
